import type { LocaleCode } from "@repo/shared/types";
import {
  type DynamicGenerationErrorReason,
  type DynamicGenerationMeta,
  type GeneratedPositionItem,
  sustainabilityCriterionText,
} from "@repo/shared/types";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./dynamic-composition-view.i18n";
import { PositionNoteBadges } from "./position-note-badges";

/** The keys of the generation error texts (all take the same params). */
type GenerationErrorTextKey =
  | "errorNoRiskAssessment"
  | "errorNoDefinition"
  | "errorNoDefinitionForPlatform"
  | "errorIncompleteDefinition"
  | "errorNoProductForPreference"
  | "errorNoEligibleProductForPreference"
  | "errorSustainabilityNotMatched";

// Every generation error reason has its own text; a new reason fails to compile here.
const ERROR_TEXT_KEY: Record<DynamicGenerationErrorReason, GenerationErrorTextKey> = {
  no_risk_assessment: "errorNoRiskAssessment",
  no_dynamic_portfolio_for_score: "errorNoDefinition",
  no_dynamic_portfolio_for_platform: "errorNoDefinitionForPlatform",
  dynamic_portfolio_incomplete: "errorIncompleteDefinition",
  no_product_for_preference: "errorNoProductForPreference",
  no_eligible_product_for_preference: "errorNoEligibleProductForPreference",
  sustainability_not_matched: "errorSustainabilityNotMatched",
};

type DynamicCompositionViewProps = {
  positions: GeneratedPositionItem[];
  generation: DynamicGenerationMeta | null;
};

/**
 * QP-185 — read-only list of a dynamic portfolio's generated composition
 * (product, weight), shared by the advisor and public surfaces.
 * A failed generation renders an explanatory banner instead of rows. No edit
 * affordances — switching to a custom portfolio is the escape hatch.
 *
 * QL-584 — both surfaces show why a position is there (what it replaces) and
 * which product blocks Submit.
 */
export function DynamicCompositionView({ positions, generation }: DynamicCompositionViewProps) {
  const { userLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);
  const lang = userLanguage as LocaleCode;
  const localize = useLocalize();

  if (generation?.status === "error") {
    const reason = generation.errorReason;
    // A stored generation can carry a reason that no longer exists (instances the
    // reset migration skipped), so the lookup may miss at runtime despite the type.
    const textKey: GenerationErrorTextKey | undefined = reason ? ERROR_TEXT_KEY[reason] : undefined;
    return (
      <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
        {textKey
          ? t[textKey]({
              preference: generation.errorPreference
                ? localize(generation.errorPreference.preferenceNameI18n)
                : "",
              preferences: (generation.errorSustainabilityCriteria ?? [])
                .map((criterion) => sustainabilityCriterionText(criterion, lang))
                .join(", "),
            })
          : t.errorGeneric}
      </div>
    );
  }

  if (generation?.status !== "ok" || positions.length === 0) return null;

  const unsuitableNames = positions
    .filter((position) => position.annotations?.unsuitableNoReplacement)
    .map((position) => localize(position.productNameI18n));

  return (
    <div className="space-y-2">
      <div className="font-medium">{t.generatedComposition}</div>
      {unsuitableNames.length > 0 && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.unsuitableBlocked(unsuitableNames.join(", "))}
        </div>
      )}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50 text-left">
              <th className="p-3 font-medium">{t.product}</th>
              <th className="p-3 font-medium text-right">{t.weight}</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <tr key={position.id} className="border-b last:border-b-0">
                <td className="p-3">
                  {localize(position.productNameI18n)}
                  {position.isin && (
                    <span className="text-muted-foreground ml-2">{position.isin}</span>
                  )}
                  <PositionNoteBadges annotations={position.annotations} />
                </td>
                <td className="p-3 text-right">
                  {formatNumber(position.weight, { maximumFractionDigits: 2 })}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
